import type { Course, WordView } from "./content/course.ts";
import { charOf, fitsStep, has, reachOf } from "./content/course.ts";
import type {
	Item,
	LessonDef,
	ProgressKey,
	RecipeStep,
	SkillId,
	UnitDef,
} from "./content/types.ts";
import { itemIdOf, progressKey, skillOf } from "./content/types.ts";
import { makeMatch } from "./exercises/match/logic.ts";
import { exerciseType } from "./exercises/registry.ts";
import type { StepConfig } from "./exercises/registry.ts";
import type { Face } from "./face.ts";
import { PRACTICE, practiceKeys, practiceUnits, type PracticeMode } from "./practice.ts";
import { isDue, keyLevel, started } from "./progress/query.ts";
import { reviewStep, sentencesOf, type ReviewPools } from "./review-table.ts";
import { pickUnique, sample, shuffle, spread, uid, weightedDraw } from "./rng.ts";
import type { GameState } from "./state.ts";
import "./exercises/index.ts";
import { TRACK_RULES } from "./tracks.ts";

/** l2s letter→sound, s2l sound→letter, u2l capital→lowercase, l2u lowercase→capital, h2l hear→letter */
export type ChoiceMode = "l2s" | "s2l" | "u2l" | "l2u" | "h2l";

export interface Option {
	id: string;
	face: Face;
	/** The recording a text face stands for, played when its tile is tapped: a
	 *  reading is taught as a sound, never as a spelling to learn for itself. */
	sound?: string;
}

export interface IntroStep {
	kind: "intro";
	key: ProgressKey;
	uid: string;
}

export interface ChoiceStep {
	kind: "choice";
	mode: ChoiceMode;
	key: ProgressKey;
	answer: string;
	options: Option[];
	uid: string;
}

export interface WordStep {
	kind: "word";
	word: WordView;
	/** What this step trains — see word/logic.ts's `keys` for why it exists. */
	key: ProgressKey;
	/** The word's item id: option ids are item ids, language-neutral. */
	answer: string;
	options: Option[];
	/** A listening step: the word is heard, and the options are spellings. */
	listen?: boolean;
	uid: string;
}

export interface MeaningStep {
	kind: "meaning";
	/** `read` prompts with the script, silent; `hear` with the recording. */
	mode: "read" | "hear";
	word: WordView;
	key: ProgressKey;
	/** The word's item id: options are keyed by item id, so equal glosses could never collide. */
	answer: string;
	options: Option[];
	uid: string;
}

export interface SpellStep {
	kind: "spell";
	word: WordView;
	key: ProgressKey;
	/** The word's letters as item ids, in order; ու is one. */
	letters: string[];
	/** Ids are `<letterId>#<n>`: a repeated letter needs two distinct tiles. */
	bank: Option[];
	uid: string;
}

export interface MatchStep {
	kind: "match";
	left: Option[];
	right: Option[];
	uid: string;
}

export interface ComposeStep {
	kind: "compose";
	/** The correct sentence, in order, resolved for display. */
	tokens: { id: string; face: Face }[];
	/** Indices into `tokens` the learner must fill. Every other index is given. */
	blanks: number[];
	/** The chips offered. Ids are item ids, so this is language-neutral. */
	bank: Option[];
	key: ProgressKey;
	/** The L1 translation: the prompt for a build, a subtitle for a cloze. */
	gloss: string;
	/** The sentence's audio asset, when the drawn sentence has one. */
	sound?: string;
	/**
	 * A review shows one prompt only: the recording (by ear) or the gloss (by
	 * meaning). Absent, both show, as in a lesson.
	 */
	prompt?: "sound" | "gloss";
	uid: string;
}

export interface ExplainStep {
	kind: "explain";
	/**
	 * Paradigm rows, already sorted by the form items' `order`. `lead` is null
	 * for a paradigm with no leading column — the `-ը/-ն` rule will have none —
	 * and the view renders two columns then.
	 */
	rows: { itemId: string; lead: Face | null; form: Face; gloss: string }[];
	rule: string;
	order: string;
	watch: string;
	uid: string;
}

export type LessonStep =
	| IntroStep
	| ChoiceStep
	| WordStep
	| MatchStep
	| ComposeStep
	| ExplainStep
	| MeaningStep
	| SpellStep;

/**
 * Exhaustiveness helper for `LessonStep` (and any other closed union) consumers.
 * Call it in the `default`/final branch of a `switch`/`if`-chain over `step.kind`.
 * If every member is handled, TypeScript narrows `step` to `never` there, so
 * this compiles; add a fifth `LessonStep` kind and every such call site fails
 * to typecheck until it grows a case for it. If one somehow runs anyway (a
 * value that bypassed the type system, e.g. from JSON), it throws loudly
 * instead of silently falling through.
 */
export function assertNever(x: never): never {
	throw new Error(`Unexpected value: ${JSON.stringify(x)}`);
}

/**
 * Every unit `unit` reaches (see `reachOf`) — its own track's, plus
 * word-sharing tracks' for a course-reach unit. `list` picks which of each
 * unit's lists to resolve.
 */
function trackItems(
	unit: UnitDef,
	course: Course,
	list: "teaches" | "practises",
	upToAndIncluding: boolean,
): Item[] {
	return reachOf(unit, course.units, upToAndIncluding).flatMap((u) => course.itemsOf(u, list));
}

/**
 * The pool an exercise builds its tiles/options from: its confusion group
 * first (deduplicated), then every other item of the same kind in scope.
 *
 * The list comes from the step's own `draw` rather than being guessed from
 * the drilled item's kind, which is what the previous version did
 * (`item.kind === "letter" ? "teaches" : "practises"`) — a guess that has no
 * right answer for a third item kind.
 *
 * Exported for tests.
 */
export function poolFor(item: Item, unit: UnitDef, course: Course, r: RecipeStep): string[] {
	const items =
		r.pool === "own"
			? course.itemsOf(unit, r.draw)
			: r.pool === "course"
				? course.units
						.slice(0, course.units.findIndex((u) => u.id === unit.id) + 1)
						// Other capitals units' names stay out, as in reach: see TrackRules.sharesWords.
						.filter((u) => u.id === unit.id || TRACK_RULES[u.track].sharesWords)
						.flatMap((u) => course.itemsOf(u, r.draw))
				: trackItems(unit, course, r.draw, true);
	const itemIds = new Set(items.map((i) => i.id));
	const group = (course.confusions.find((g) => g.includes(item.id)) ?? []).filter((id) =>
		itemIds.has(id),
	);
	const related = group.filter((id) => id !== item.id);
	const rest = items.map((i) => i.id).filter((id) => id !== item.id && !related.includes(id));
	return [...related, ...rest];
}

/**
 * Which items a step may quiz: its `draw` list from this unit, or — with
 * `scope: "earlier"` — the same list from every earlier unit it reaches. A
 * unit with no earlier unit it reaches resolves to an empty list;
 * `weightedDraw`/`sample` both already treat an empty pool as "draw
 * nothing", so this needs no special-casing at the call site.
 */
function candidatesFor(unit: UnitDef, lesson: LessonDef, course: Course, r: RecipeStep): Item[] {
	if (r.scope === "earlier") return trackItems(unit, course, r.draw, false);
	if (r.draw === "practises" && lesson.items) return lesson.items.map(course.itemById);
	return course.itemsOf(unit, r.draw);
}

/** One built step and the recipe step that produced it. */
export interface BuiltStep {
	step: LessonStep;
	cfg: StepConfig;
	/** Index into the recipe's `steps`, so a caller can count per step. */
	recipeStep: number;
}

/**
 * `buildLesson` with provenance. One implementation, two views — the app
 * takes the steps, the validator takes the provenance it needs to check that
 * each recipe step produced what it declared and to call each type's own
 * `check` with the config it was built from. Two separate implementations
 * would drift, and the drift would be invisible.
 */
export function buildLessonTraced(
	lesson: LessonDef,
	unit: UnitDef,
	st: GameState,
	course: Course,
): BuiltStep[] {
	const recipe = course.recipeById(lesson.recipe);
	const out: BuiltStep[] = [];

	recipe.steps.forEach((r, recipeStep) => {
		const type = exerciseType(r.type);
		const skill = r.skill ?? recipe.skill;
		const cfg: StepConfig = { skill, options: r.options ?? {} };
		const candidates = candidatesOf(unit, lesson, course, r);
		const options = { ...r.options, skill };
		const build = (item: Item) =>
			out.push({
				step: type.build({
					itemId: item.id,
					pool: poolFor(item, unit, course, r),
					options,
					course,
					unit,
				}),
				cfg,
				recipeStep,
			});

		if (r.forEach === "new") {
			candidates.forEach(build);
			return;
		}
		const drawn =
			r.weightBy === "weakness"
				? weightedDraw(candidates, (i) => 6 - keyLevel(st, progressKey(i.id, skill)), r.count ?? 1)
				: sample(candidates, r.count ?? 1);
		drawn.forEach(build);
	});
	return out;
}

export const buildLesson = (
	lesson: LessonDef,
	unit: UnitDef,
	st: GameState,
	course: Course,
): LessonStep[] => buildLessonTraced(lesson, unit, st, course).map((b) => b.step);

/**
 * The items a recipe step may quiz, after the exercise type's own kind
 * filter. Exported so the validator can check a step's declared count
 * against what its pool can actually supply, rather than only against what
 * came out.
 */
export function candidatesOf(
	unit: UnitDef,
	lesson: LessonDef,
	course: Course,
	r: RecipeStep,
): Item[] {
	const type = exerciseType(r.type);
	return candidatesFor(unit, lesson, course, r).filter(
		(i) => type.appliesTo.includes(i.kind) && fitsStep(i, r),
	);
}

/**
 * Review distractor pools over the units the learner has met: a letter they
 * have never met is a free elimination, and on screen it reads as a mistake.
 * Letters only in the letter pools, recorded words only in the word pool — a
 * grammar unit's forms and sentences have no place among a choice's options.
 */
export function reviewPools(allMet: readonly UnitDef[], course: Course): ReviewPools {
	// Grammar units feed sentences only: their words and letters are no distractors.
	const met = allMet.filter((u) => TRACK_RULES[u.track].reviewPool !== null);
	const bySkill = (skill: SkillId) => [
		...new Set(
			met
				.flatMap((u) => course.unitKeys(u))
				.filter((k) => skillOf(k) === skill && course.itemById(itemIdOf(k)).kind === "letter")
				.map(itemIdOf),
		),
	];
	const practised = (units: readonly UnitDef[]) => [
		...new Set(
			units
				.flatMap((u) => u.practises)
				.filter((id) => {
					const it = course.itemById(id);
					return it.kind === "word" && it.audio !== undefined;
				}),
		),
	];
	return {
		sentences: sentencesOf(allMet, course),
		lower: bySkill("lower"),
		upper: bySkill("upper"),
		words: practised(met),
		glossWords: practised(met.filter((u) => TRACK_RULES[u.track].sharesWords)),
	};
}

/**
 * A practice round in one mode (practice.ts). Not unit-scoped, so it has no
 * recipe: it keeps its own fixed shape over every unit the learner has opened.
 * Words are drawn by weakness like letters, not as fixed extra steps.
 *
 * Mix is the round the Learn map used to offer, to the Math.random call: the
 * census pins it, so keep the draw's order of operations as it is.
 */
export function buildPractice(
	mode: PracticeMode,
	st: GameState,
	course: Course,
	now: number = Date.now(),
): LessonStep[] {
	const all = practiceKeys(mode, st, course, now);
	const pools = reviewPools(practiceUnits(st, course), course);
	if (mode === "mistakes") {
		// A miss is the reason to be in this round, due or not: newest first.
		// Build first, then cap: an unbuildable slip must not shrink the round.
		return [...all]
			.sort((a, b) => (st.slips[b] ?? 0) - (st.slips[a] ?? 0))
			.flatMap((k) => reviewStep(k, pools, course) ?? [])
			.slice(0, 10);
	}
	// Due keys first, weakest first: a round exists to catch what is fading.
	const due = all
		.filter((k) => isDue(st, k, now))
		.sort((a, b) => keyLevel(st, a, now) - keyLevel(st, b, now));
	const rest = all.filter((k) => !due.includes(k));
	const w = (k: ProgressKey) => (6 - keyLevel(st, k, now)) ** 2;
	const picks = [
		...due.slice(0, 10),
		...spread(weightedDraw(rest, w, Math.max(0, 10 - due.length))),
	];
	// A `use` key with no practised sentence in an open unit builds nothing: dropped.
	const qs = picks.flatMap((k) => reviewStep(k, pools, course, PRACTICE[mode].sentence) ?? []);
	// Drawn from letters' :lower keys only, so no letter can appear twice on the board.
	const matchIds = PRACTICE[mode].match
		? pickUnique(
				all.filter((k) => skillOf(k) === "lower" && course.itemById(itemIdOf(k)).kind === "letter"),
				w,
				4,
			).map(itemIdOf)
		: [];
	const steps: (LessonStep | null)[] = [
		...qs.slice(0, 4),
		matchIds.length === 4 ? makeMatch(matchIds, "lower", 4, course, "audio") : null,
		...qs.slice(4),
	];
	return steps.filter((s): s is LessonStep => s !== null);
}

/**
 * A review of exactly these keys — the "Review these" action after a
 * checkpoint and a unit's Review badge. Each key is quizzed by the review
 * table's builder for its item kind and skill; a key the table cannot quiz (no
 * builder, or a form with no practised sentence yet) is skipped rather than guessed at. Capped at ten so
 * a long list stays a bite-sized round.
 */
export function buildKeyReview(
	keys: readonly ProgressKey[],
	st: GameState,
	course: Course,
): LessonStep[] {
	const pools = reviewPools(
		course.units.filter((u) => started(st, u)),
		course,
	);
	const steps: LessonStep[] = [];
	for (const k of keys) {
		if (steps.length === 10) break;
		const s = reviewStep(k, pools, course);
		if (s) steps.push(s);
	}
	return steps;
}

/**
 * A word for an intro card that contains the letter just introduced.
 * Rewritten against `Course` (it used to walk data.ts's `WORDS`/`unitOfKey`):
 * find a word among the unit's own `practises` whose text contains the
 * letter, falling back to any lowercase unit's practised words.
 */
export function exampleWord(key: ProgressKey, course: Course): WordView | null {
	const itemId = itemIdOf(key);
	const letter = course.itemById(itemId);
	if (letter.kind !== "letter") throw new Error(`exampleWord: not a letter item: ${itemId}`);

	const findIn = (units: UnitDef[], matches: (text: string) => boolean): WordView | null => {
		for (const u of units) {
			for (const w of course.itemsOf(u, "practises")) {
				if (w.kind === "word" && matches(w.text)) return course.views.word(w.id);
			}
		}
		return null;
	};

	if (skillOf(key) === "upper") {
		const capital = letter.upper;
		if (!capital) return null;
		return findIn(
			course.units.filter((u) => TRACK_RULES[u.track].letterCase === "caps"),
			(text) => text.startsWith(capital),
		);
	}

	const l = charOf(itemId);
	const lower = (u: UnitDef) => TRACK_RULES[u.track].letterCase === "lower";
	const home = course.units.find((u) => lower(u) && u.teaches.includes(itemId));
	const matches = (text: string) => has(text, l);
	return (home && findIn([home], matches)) || findIn(course.units.filter(lower), matches);
}
